import {
  ProjectChangeType,
  type ProjectChange,
} from '@shared/helpers/pipeline/update-engine.models';
import { getElementById } from '@shared/helpers/recursive-element.helpers';
import { getElementProductCategory } from '@shared/helpers/element-category.helpers';
import {
  createMarketEstimatesSnapshot,
  getProductEstimates,
  percentileToFactor,
} from '@shared/helpers/product-market-estimates.helpers';
import {
  ElementKind,
  type Baseline,
  type IProductElement,
  type ProductMarketEstimate,
  type ProjectMarketEstimates,
} from '@shared/models/project.model';
import type { ProductID } from '@shared/models/product.model';
import { getProductClimateImpactLevelNumber } from '@shared/helpers/products/product-climate-impact-level.helpers';

export type CarbonProductOverrideUiSlice = {
  rowTitle: string;
  rowValue: string;
  rowUnit?: string;
};

const resolveEstimateForProductElement = (
  marketEstimates: ProjectMarketEstimates,
  genericProductId: ProductID | undefined,
): ProductMarketEstimate => {
  if (genericProductId === undefined) return marketEstimates.defaults;
  return (
    getProductEstimates(marketEstimates, genericProductId) ??
    marketEstimates.defaults
  );
};

/**
 * UI slice for proposal action rows when the change sets the CO₂ market percentile override.
 *
 * @param marketEstimates - Project snapshot; falls back to {@link createMarketEstimatesSnapshot}
 *                          when omitted (same pattern as product bulk edit).
 */
export const getCarbonProductOverrideUiSlice = (
  change: ProjectChange,
  version: Baseline | undefined,
  marketEstimates?: ProjectMarketEstimates,
): CarbonProductOverrideUiSlice | undefined => {
  if (change.type !== ProjectChangeType.ProductElementCarbonLevelOverride) {
    return undefined;
  }

  const percentile = change.percentile;
  const estimatesSnapshot = marketEstimates ?? createMarketEstimatesSnapshot();

  let genericProductId: ProductID | undefined;
  if (version) {
    const el = getElementById(version, change.elementId, false);
    if (el?.kind === ElementKind.Product) {
      const pe = el as IProductElement;
      genericProductId = pe.generic_product_id ?? pe.product_id;
    }
  }

  const categoryId =
    genericProductId !== undefined
      ? getElementProductCategory(genericProductId)
      : undefined;

  const estimate = resolveEstimateForProductElement(
    estimatesSnapshot,
    genericProductId,
  );

  const pctRounded =
    percentile !== undefined
      ? Math.round(percentileToFactor(percentile, estimate.carbon) * 100)
      : undefined;

  if (pctRounded !== undefined) {
    const levelNum = getProductClimateImpactLevelNumber(categoryId, pctRounded);
    const pctStr = `${pctRounded > 0 ? '+' : ''}${pctRounded}%`;
    return {
      rowTitle: 'Carbon factor',
      rowValue: `Level ${levelNum} (${pctStr})`,
    };
  }

  if (percentile !== undefined) {
    return {
      rowTitle: 'Carbon factor',
      rowValue: String(Math.round(percentile)),
    };
  }

  return {
    rowTitle: 'Carbon factor',
    rowValue: '—',
  };
};

/**
 * UI slice for transport override rows.
 */
export const getTransportOverrideUiSlice = (
  change: ProjectChange,
): CarbonProductOverrideUiSlice | undefined => {
  if (change.type !== ProjectChangeType.ProductElementTransportOverride) {
    return undefined;
  }
  if (change.value === undefined) return undefined;
  return {
    rowTitle: 'Transport',
    rowValue: String(change.value),
    rowUnit: 'km',
  };
};

/**
 * UI slice for waste/spill override rows.
 */
export const getWasteOverrideUiSlice = (
  change: ProjectChange,
): CarbonProductOverrideUiSlice | undefined => {
  if (change.type !== ProjectChangeType.ProductElementWasteOverride) {
    return undefined;
  }
  if (change.value === undefined) return undefined;
  return {
    rowTitle: 'Spill',
    rowValue: String(change.value),
    rowUnit: '%',
  };
};

/**
 * UI slice for Transport (TRANSPORT_KM) and Spill (WASTE_PERCENT) override rows.
 * Returns `undefined` for all other change types or override targets.
 */
export const getTransportOrSpillOverrideUiSlice = (
  change: ProjectChange,
): CarbonProductOverrideUiSlice | undefined =>
  getTransportOverrideUiSlice(change) ?? getWasteOverrideUiSlice(change);
